import { ReactNode } from 'react'
import { getFormValues } from 'redux-form'
import * as isNumber from 'lodash/isNumber'
import * as omit from 'lodash/omit'
import * as omitBy from 'lodash/omitBy'
import * as values from 'lodash/values'
import * as identity from 'lodash/identity'
import {
  Peergroup,
  PeergroupFormField,
  PeergroupFilterName,
  PeergroupFormType,
  RawPeergroupRelation,
  PeergroupRelation,
  PeergroupRelationCollection,
  PeergroupDefinition,
} from 'interfaces/peergroups'
import { IdRangeEntity, AnyDict, Omit } from 'interfaces'
import { LocationOption } from 'interfaces/locations'
import { State } from 'interfaces/state'
import { FORM_CREATE_CUSTOM_PEER_GROUP, FORM_EDIT_CUSTOM_PEER_GROUP } from 'constants/forms'
import { SLUGS, DEFAULT_PROP_VALUE } from 'constants/peergroups'
import { locationToOption } from 'helpers/cityHelpers'
import { PeerDefinitionProps } from 'components/features/pfm/PeerDefinition/PeerDefinition'
import { formatAgeRange, shortenMoneyRange, formatAreaType, formatCreditRange, formatHousingType } from 'helpers/formatHelpers'

type PeergroupFormFieldMap = Record<PeergroupFilterName, PeergroupFormField>

export const peergroupFiltersMap: PeergroupFormFieldMap = {
  age_range: {
    name: 'age_range',
    label: 'Age Range',
    type: 'select',
  },
  location: {
    name: 'location',
    label: 'Location',
    type: 'search',
  },
  area_type: {
    name: 'area_type',
    label: 'Location Type',
    type: 'select',
  },
  creditscore_range: {
    name: 'creditscore_range',
    label: 'Credit Score',
    type: 'select',
  },
  income_range: {
    name: 'income_range',
    label: 'Income',
    type: 'select',
  },
  housing_type: {
    name: 'housing_type',
    label: 'Housing Type',
    type: 'select',
  },
}


export const allPeergroupFilters: PeergroupFormField[] = values(peergroupFiltersMap)

export const PEER_GROUP_FORM_FILTER_PREFIX = 'peergroup'
export const getPeergroupFieldByFilter = (filter: PeergroupFilterName) =>
  `${ PEER_GROUP_FORM_FILTER_PREFIX }.${ filter }`

export const getPeergroupFilters = (peergroup: Peergroup) =>
  allPeergroupFilters.filter(({ name }) => Boolean(peergroup[name]))

export function getPeergroupFilterNames (peergroup: Peergroup): PeergroupFilterName[] {
  return getPeergroupFilters(peergroup).map((item) => item.name)
}

export function getMemberRelation (relations: PeergroupRelationCollection): PeergroupRelation | null {
  const peergroupId = relations.allKeys.find((id) => relations.byKey[id].slug === SLUGS.peers)

  return isNumber(peergroupId) ?
    relations.byKey[peergroupId] :
    null
}

export function getDefaultRelation (relations: PeergroupRelationCollection): PeergroupRelation | null {
  const defaultId = relations.allKeys.find((id) =>
    isDefaultRelation(relations.byKey[id]))

  return isNumber(defaultId)
    ? relations.byKey[defaultId]
    : null
}

export function isDefaultRelation (relation: PeergroupRelation): boolean {
  return relation.slug === SLUGS.everyone
}

/**
 * Helper to extract get relation (without peergroup, but with peergroupId)
 * from member-peergroup-relation response
 */
export const extractRelation = (item: RawPeergroupRelation): PeergroupRelation => {
  const peergroupId = item.peergroup.id
  const { peergroup, ...restItem } = item
  return {
    ...restItem,
    peergroupId,
  }
}

export const isPeergroupForm = (form: string): form is PeergroupFormType =>
  form === FORM_CREATE_CUSTOM_PEER_GROUP || form === FORM_EDIT_CUSTOM_PEER_GROUP

export const locationOptionToValue = (location: LocationOption) => ({
  id: location.value,
  type: location.type,
})


export function getFormPeerFiltersInitialValues (peergroup: Peergroup) {
  return getPeergroupFilters(peergroup)
    .reduce((result: any, { name }: PeergroupFormField) => {
      if (name === 'location' && peergroup.location) {
        result[name] = locationToOption(peergroup.location)
      } else if (name === 'housing_type' || name === 'area_type') {
        const value = peergroup[name]
        result[name] = value
      } else {
        const { id } = peergroup[name] as IdRangeEntity
        result[name] = id
      }

      return result
    }, {})
}

export function preparePeergroupQuery (peergroup: AnyDict) {
  const query: AnyDict = omitBy(peergroup, (item) => item === null)
  if (query['location']) {
    query['location'] = locationOptionToValue(query['location'])
  }

  return query
}

export const buildFindPeergroupQuery = (state: State, form: string, filterName: PeergroupFilterName, value: any = null): Record<string, any> => {
  const stateValues = (getFormValues(form)(state) as any).peergroup
  const queryValues = value === null || value === undefined
    ? omit(stateValues, filterName)
    : {
      ...stateValues,
      [filterName]: value,
    }

  return preparePeergroupQuery(queryValues)
}

type RenderPropFunction = (value: ReactNode, key: keyof PeergroupDefinition) => ReactNode
export const preparePeerDefinitionProps = (definition: PeergroupDefinition, renderProp: RenderPropFunction = identity): Omit<PeerDefinitionProps, 'isRange'> => {
  const {
    age_range,
    income_range,
    location,
    area_type,
    creditscore_range,
    housing_type,
  } = definition

  return {
    age: renderProp(age_range ? formatAgeRange(age_range) : DEFAULT_PROP_VALUE, 'age_range'),
    income: renderProp(income_range ? shortenMoneyRange(income_range) : DEFAULT_PROP_VALUE, 'income_range'),
    location: renderProp(location ? location.name : DEFAULT_PROP_VALUE, 'location'),
    area_type: renderProp(area_type ? formatAreaType(area_type) : DEFAULT_PROP_VALUE, 'area_type'),
    creditscore: renderProp(creditscore_range ? formatCreditRange(creditscore_range) : DEFAULT_PROP_VALUE, 'creditscore_range'),
    housing_type: renderProp(formatHousingType(housing_type), 'housing_type'),
  }
}
